/**
 * src/store/modules/user.js
 *
 * Purpose: the `user` namespaced Vuex module — the auth / session / permissions
 * core. Re-authored equivalent of the deployed pro-ui `user` module (OLD
 * `chunk-vendors/7689.js`, LIVE `Geofence.js` catch-all chunk).
 *
 * VERBATIM contract (re-greppable):
 *   - actions POST `user/auth`, `user/get_info`, `user/logout`,
 *     `subuser/session/create`, `user/audit/checkin`
 *   - `user/auth` LIVE returns `{type, hash}`, branches on the type enum
 *     "authenticated" / "second_factor_required"
 *   - session lives in the `session_key` cookie (30-day) via `setSession`
 *   - get_info re-polls on a 60s (6e4) interval
 *   - getter `hasPermissions(permOrArray)`: master -> true; subuser -> holds ALL
 *     requested rights (intersection === requested)
 *   - `subuser` mutation shape `{isSubuser, masterInfo, rights}`
 *   - impersonation parks the master session in localStorage["master-session-key"].
 * Wiring / mutation bodies are re-authored.
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim,
 * internal impl re-authored.
 */

import dayjs from 'dayjs';
import intersection from 'lodash/intersection';

import { request } from '../../api/request.js';
import CONFIG from '../../api/config.js';
import {
  cookie,
  getSessionKey,
  getMasterSessionKey,
  setMasterSessionKey,
  clearMasterSession,
  setMasterLocale,
  SESSION_KEY_COOKIE,
  CHECK_AUDIT_COOKIE,
  MASTER_SESSION_KEY
} from '../../api/cookies.js';
import { getRouter } from '../../api/context.js';

// VERBATIM type-enum strings from LIVE `Geofence.js`.
export const AUTH_TYPES = {
  AUTHENTICATED: 'authenticated',
  SECOND_FACTOR_REQUIRED: 'second_factor_required'
};

// Module-scoped interval handle for the user/get_info poller. Kept outside Vuex
// state so it is never proxied/serialized.
let userInfoPoller = null;

function stopUserInfoPoll() {
  if (userInfoPoller) {
    clearInterval(userInfoPoller);
    userInfoPoller = null;
  }
}

function redirectToLogin() {
  const router = getRouter();
  if (router && typeof router.push === 'function') {
    const p = router.push('/login');
    if (p && typeof p.catch === 'function') p.catch(function () {});
    return;
  }
  // Fallback for the hash-router login page (Config.appRedirect.loginPage).
  if (typeof window !== 'undefined' && window.location) {
    window.location.hash = '#/login';
  }
}

function createInitialState() {
  return {
    user: null,
    settings: {},
    allowed_maps: [],
    subuser: {
      isSubuser: false,
      masterInfo: {},
      rights: []
    },
    ready: false,
    // phone-verification resend throttle
    resendBlocked: false
  };
}

const state = createInitialState();

const getters = {
  /**
   * The single permission gate. VERBATIM semantics:
   *   master (or empty request) -> allowed;
   *   subuser -> allowed only if it holds EVERY requested right (AND).
   */
  hasPermissions: function (state) {
    return function (perm, _n) {
      const isSub = state.subuser.isSubuser;
      const rights = state.subuser.rights;
      const list = Array.isArray(perm) ? perm : [perm];
      if (!isSub || !perm) return true;
      if (list.find(function (p) { return typeof p !== 'string'; })) {
        throw new TypeError('There are non-string parameters');
      }
      return intersection(rights, list).length === list.length;
    };
  },

  isSubuser: function (state) {
    return state.subuser.isSubuser;
  },

  masterInfo: function (state) {
    return state.subuser.masterInfo;
  },

  rights: function (state) {
    return state.subuser.rights;
  },

  isReady: function (state) {
    return state.ready;
  },

  isDemo: function (state) {
    return !!(state.user && state.user.demo);
  },

  // subuser shows the master's balance
  balance: function (state) {
    if (state.subuser.isSubuser && state.subuser.masterInfo) {
      return state.subuser.masterInfo.balance;
    }
    return state.user ? state.user.balance : undefined;
  },

  title: function (state) {
    if (!state.user) return '';
    return ((state.user.first_name || '') + ' ' + (state.user.last_name || '')).trim();
  },

  userId: function (state) {
    return state.user ? state.user.id : undefined;
  },

  allowedMaps: function (state) {
    return state.allowed_maps;
  },

  resendBlocked: function (state) {
    return state.resendBlocked;
  }
};

const mutations = {
  setUser: function (state, user) {
    state.user = user;
  },

  setSettings: function (state, settings) {
    state.settings = settings || {};
  },

  setAllowedMaps: function (state, maps) {
    state.allowed_maps = maps || [];
  },

  /**
   * VERBATIM subuser mutation shape. `master` = owner account (present only for
   * subuser sessions); `privileges.rights` = granted permission strings. Also
   * self-cleans a stale master-session-key when the current session is NOT a
   * subuser.
   */
  subuser: function (state, payload) {
    const p = payload || {};
    const master = p.master;
    const privileges = p.privileges;
    state.subuser = {
      isSubuser: !!master,
      masterInfo: master || {},
      rights: (privileges && privileges.rights) || []
    };
    if (!state.subuser.isSubuser && getMasterSessionKey()) {
      // stale master key parked from a prior impersonation
      clearMasterSession();
    }
  },

  setReady: function (state, value) {
    state.ready = value !== false;
  },

  blockResend: function (state, value) {
    state.resendBlocked = value !== false;
  },

  resetState: function (state) {
    const fresh = createInitialState();
    Object.keys(fresh).forEach(function (k) {
      state[k] = fresh[k];
    });
  }
};

const actions = {
  /**
   * user/auth — LIVE, MFA-aware. Returns `{type, hash}` and branches:
   *   "authenticated"           -> setSession + loadUserWithPlugins
   *   "second_factor_required"  -> hand the short-lived hash to `mfa-store`,
   *                                open the OTP dialog (loginAction deferred).
   * Falls back to the OLD contract (a bare hash string / `{hash}`) when the
   * server does not send a `type`.
   */
  auth: async function (ctx, credentials) {
    const dispatch = ctx.dispatch;

    await dispatch('mfa-store/startMfaTimeout', null, { root: true }).catch(function () {});

    const res = await request('user/auth', credentials); // {type, hash}

    if (res && res.type === AUTH_TYPES.AUTHENTICATED) {
      await dispatch('mfa-store/clearMfaTimeout', null, { root: true }).catch(function () {});
      await dispatch('setSession', res.hash);
      await dispatch('loadUserWithPlugins');
      return res;
    }

    if (res && res.type === AUTH_TYPES.SECOND_FACTOR_REQUIRED) {
      await dispatch('mfa-store/setHash', res.hash, { root: true });
      await dispatch('mfa-store/toggleDialog', true, { root: true });
      return res;
    }

    // OLD contract: server returned the hash directly (string or {hash}).
    const hash = typeof res === 'string' ? res : res && res.hash;
    await dispatch('mfa-store/clearMfaTimeout', null, { root: true }).catch(function () {});
    await dispatch('setSession', hash);
    await dispatch('loadUserWithPlugins');
    return res;
  },

  /**
   * Persist the session. VERBATIM: `session_key` cookie, path "/", +30 days
   * (dayjs). This is the value that later travels as `hash` on every call.
   */
  setSession: function (_ctx, hash) {
    cookie(SESSION_KEY_COOKIE, hash, {
      path: '/',
      expires: dayjs().add(30, 'days').toDate()
    });
    return Promise.resolve(hash);
  },

  // LIVE alias — post-auth this also boots the per-account plugins; here it
  // simply drives the get_info poller.
  loadUserWithPlugins: function (ctx) {
    return ctx.dispatch('loadUser');
  },

  /**
   * Open the `user/get_info` polling subscription (60s) and commit the payload.
   * A session-invalid code inside the poll trips the request-layer auto-logout.
   */
  loadUser: function (ctx) {
    const dispatch = ctx.dispatch;
    stopUserInfoPoll();

    const poll = function () {
      return dispatch('getUser')
        .then(function (data) {
          // applyUserInfo is an ACTION (it commits setUser/setSettings/subuser/…),
          // so it must be dispatched, not committed.
          dispatch('applyUserInfo', data);
          return data;
        })
        .catch(function () {
          /* auto-logout (if session-invalid) is handled by the request layer */
        });
    };

    return poll().then(function () {
      userInfoPoller = setInterval(poll, CONFIG.userInfoPollInterval); // 6e4
    });
  },

  // The API-service `getUser()` -> user/get_info.
  getUser: function () {
    return request('user/get_info');
  },

  // Commit a user/get_info envelope into the module.
  applyUserInfo: function (ctx, data) {
    const d = data || {};
    const info = d.user_info || {};
    ctx.commit('setUser', Object.assign({}, info, { dealer_id: info.paas_id }));
    if (d.paas_settings) ctx.commit('setSettings', d.paas_settings);
    if (d.tariff_restrictions) ctx.commit('setAllowedMaps', d.tariff_restrictions.allowed_maps);
    ctx.commit('subuser', { master: d.master, privileges: d.privileges });
    ctx.commit('setReady', true);
    if (typeof window !== 'undefined' && typeof window.ga === 'function' && info.id != null) {
      window.ga('set', 'userId', info.id);
    }
  },

  /**
   * logOut — clears the session cookies + master key, tears down the store and
   * routes to /login. Unless `noServer`, POSTs `user/logout` first.
   */
  logOut: async function (ctx, payload) {
    const noServer = payload && payload.noServer;
    if (!noServer) {
      try {
        await request('user/logout');
      } catch (e) {
        /* swallow — we log out client-side regardless */
      }
    }
    stopUserInfoPoll();
    cookie(SESSION_KEY_COOKIE, false); // delete
    cookie(CHECK_AUDIT_COOKIE, false); // delete
    clearMasterSession();

    try {
      await ctx.dispatch('clearStore', null, { root: true });
    } catch (e) {
      ctx.commit('resetState');
    }

    redirectToLogin();
  },

  /**
   * userAudit — once per service session, checkin the login and stamp
   * `check_audit` for 2h. Gated by localStorage["service_session"] and the
   * existing `check_audit` cookie.
   */
  userAudit: async function () {
    const alreadyAudited = cookie(CHECK_AUDIT_COOKIE);
    if (alreadyAudited) return;
    let serviceSession = null;
    try {
      serviceSession =
        typeof window !== 'undefined' && window.localStorage
          ? window.localStorage.getItem('service_session')
          : null;
    } catch (e) {
      serviceSession = null;
    }
    if (!serviceSession) return;
    try {
      // userCheckIn() = request("user/audit/checkin", {}, null, true)  (skipErrors)
      await request('user/audit/checkin', {}, null, true);
    } catch (e) {
      /* non-fatal */
    }
    cookie(CHECK_AUDIT_COOKIE, serviceSession, {
      path: '/',
      expires: dayjs().add(2, 'hours').toDate()
    });
  },

  /**
   * Impersonation ("log in as subuser"). VERBATIM `subuser/session/create`,
   * result key `hash`, and the parked `master-session-key` localStorage key.
   */
  impersonate: async function (ctx, item) {
    const subuserId = item && item.id != null ? item.id : item;
    const hash = await request('subuser/session/create', { subuser_id: subuserId }, 'hash');
    // park the master session before becoming the subuser
    setMasterSessionKey(getSessionKey());
    setMasterLocale((ctx.state.settings && ctx.state.settings.locale) || '');
    await ctx.dispatch('setSession', hash);
    // reload into subuser context
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
    return hash;
  },

  /**
   * returnToMaster — POST user/logout, restore the master cookie from
   * localStorage["master-session-key"], clear it, route to the subusers cabinet
   * and hard-reload.
   */
  returnToMaster: async function (ctx) {
    try {
      await request('user/logout');
    } catch (e) {
      /* ignore */
    }
    const masterKey = getMasterSessionKey();
    if (masterKey) {
      await ctx.dispatch('setSession', masterKey);
    }
    clearMasterSession();

    const router = getRouter();
    if (router && typeof router.push === 'function') {
      const p = router.push('/pro-ui/cabinet/subusers');
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
  },

  // phone-verification resend throttle helper
  blockResend: function (ctx, value) {
    ctx.commit('blockResend', value);
  }
};

export default {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions
};

// Re-export the initial-state factory so the root `clearStore` can rebuild it.
export { createInitialState, MASTER_SESSION_KEY };
